import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
} from '@mui/material';
import { BASE_URL } from '../../utils/endpoints';

const ProductCreate = ({ token, onProductCreated, handleClose }) => {
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [interests, setInterests] = useState([]);
    const [formData, setFormData] = useState({
        main_image: '',
        product_slider_images: '',
        product_title: '',
        product_price: '',
        original_price: '',
        product_description: '',
        meta_title: '',
        meta_tag: '',
        meta_description: '',
        brand: '',
        category: '',
        interests: '',
        visit_link: '',
        status: 'approved',
        search_keywords: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        setLoading(true);

        // Fetch brands
        fetch(`${BASE_URL}/brands`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                setBrands(data.brands || []);
                if (data.brands && data.brands.length > 0) {
                    setFormData((prevData) => ({ ...prevData, brand: data.brands[0].brand_name }));
                }
            })
            .catch((error) => {
                console.error('Error fetching brands:', error);
                setError('Failed to fetch brands');
            });

        // Fetch categories
        fetch('https://stage.socioglamm.com/categories', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                const categoriesArray = Array.isArray(data) ? data : data.categories || [];
                setCategories(categoriesArray);
                if (categoriesArray.length > 0) {
                    setFormData((prevData) => ({ ...prevData, category: categoriesArray[0].name }));
                }
            })
            .catch((error) => {
                console.error('Error fetching categories:', error);
                setError('Failed to fetch categories');
            });

        // Fetch interests
        fetch(`${BASE_URL}/interests`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                setInterests(data.interests || []);
                if (data.interests && data.interests.length > 0) {
                    setFormData((prevData) => ({ ...prevData, interests: data.interests[0].name }));
                }
            })
            .catch((error) => {
                console.error('Error fetching interests:', error);
                setError('Failed to fetch interests');
            })
            .finally(() => setLoading(false));
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const isValidUrl = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage('');

        // Validate URLs
        const sliderImages = formData.product_slider_images.split(',').map(item => item.trim()).filter(Boolean);
        if (!sliderImages.every(isValidUrl)) {
            setError('Please enter valid URLs for product slider images');
            setLoading(false);
            return;
        }
        if (formData.main_image && !isValidUrl(formData.main_image)) {
            setError('Please enter a valid URL for the main image');
            setLoading(false);
            return;
        }
        if (formData.visit_link && !isValidUrl(formData.visit_link)) {
            setError('Please enter a valid URL for the visit link');
            setLoading(false);
            return;
        }

        const postData = {
            main_image: formData.main_image,
            product_slider_images: sliderImages,
            product_title: formData.product_title,
            product_price: formData.product_price ? `₹ ${formData.product_price}` : "₹ 0",
            original_price: formData.original_price ? `₹ ${formData.original_price}` : "₹ 0",
            product_description: formData.product_description,
            meta_title: formData.meta_title,
            meta_tag: formData.meta_tag.split(',').map(item => item.trim()).filter(Boolean),
            meta_description: formData.meta_description,
            brand: formData.brand,
            categories: [formData.category],
            interests: formData.interests.split(',').map(item => item.trim()).filter(Boolean),
            visit_link: formData.visit_link,
            status: formData.status,
            search_keywords: formData.search_keywords.split(',').map(item => item.trim()).filter(Boolean),
        };

        ('Post Data:', postData);

        try {
            const response = await fetch(`${BASE_URL}/products`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(postData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('API Error Response:', errorData);
                throw new Error(`HTTP error! Status: ${response.status} - ${errorData.message || response.statusText}`);
            }

            const data = await response.json();
            ('Product posted:', data);
            setSuccessMessage('Product created successfully!');
            if (onProductCreated) {
                onProductCreated();
            }
            setTimeout(() => {
                handleClose();
            }, 1500);
        } catch (error) {
            console.error('Error posting product:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Typography variant="h6" sx={{ color: '#2e7d32', mb: 2 }}>
                Create New Product
            </Typography>
            {formData.main_image && (
                <Box
                    component="img"
                    src={formData.main_image}
                    sx={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                    alt="Product Image"
                    onClick={() => window.open(formData.main_image, '_blank')}
                />
            )}
            <TextField
                label="Product Title"
                name="product_title"
                value={formData.product_title}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                }}
            />
            <TextField
                label="Main Image URL"
                name="main_image"
                value={formData.main_image}
                onChange={handleChange}
                fullWidth
                size="small"
                required
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                    mb: 1,
                }}
                InputProps={{
                    endAdornment: (
                        <Button
                            size="small"
                            sx={{ color: '#2e7d32', textTransform: 'none' }}
                            onClick={() => formData.main_image && window.open(formData.main_image, '_blank')}
                            disabled={!formData.main_image}
                        >
                            Preview
                        </Button>
                    ),
                }}
            />
            <TextField
                label="Product Slider Images (comma-separated URLs)"
                name="product_slider_images"
                value={formData.product_slider_images}
                onChange={handleChange}
                fullWidth
                size="small"
                multiline
                rows={2}
                required
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                }}
                placeholder="https://example.com/img1.jpg, https://example.com/img2.jpg"
            />
            <TextField
                label="Visit Link"
                name="visit_link"
                value={formData.visit_link}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                }}
            />
            <TextField
                label="Product Description"
                name="product_description"
                value={formData.product_description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                size="small"
                required
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                    mb: 1.5,
                }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    label="Product Price"
                    name="product_price"
                    value={formData.product_price}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                    InputProps={{
                        startAdornment: <span style={{ marginRight: 4, color: '#2e7d32' }}>₹</span>,
                    }}
                />
                <TextField
                    label="Original Price"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                    InputProps={{
                        startAdornment: <span style={{ marginRight: 4, color: '#2e7d32' }}>₹</span>,
                    }}
                />
            </Box>
            <TextField
                label="Meta Title"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleChange}
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                }}
            />
            <Box>
                <TextField
                    label="Meta Tag (comma-separated)"
                    name="meta_tag"
                    value={formData.meta_tag}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                />
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formData.meta_tag.split(',').map((item) => item.trim()).filter(Boolean).map((keyword, idx) => (
                        <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                            {keyword}
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box>
                <TextField
                    label="Search Keywords (comma-separated)"
                    name="search_keywords"
                    value={formData.search_keywords}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                    placeholder="Enter keywords"
                />
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formData.search_keywords.split(',').map((item) => item.trim()).filter(Boolean).map((keyword, idx) => (
                        <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                            {keyword}
                        </Box>
                    ))}
                </Box>
            </Box>
            <TextField
                label="Meta Description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleChange}
                fullWidth
                size="small"
                multiline
                rows={3}
                InputLabelProps={{ style: { color: '#2e7d32' } }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#2e7d32' },
                        '&:hover fieldset': { borderColor: '#2e7d32' },
                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                    },
                }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel
                            sx={{
                                color: '#2e7d32',
                                '&.Mui-focused': { color: '#2e7d32' },
                                '&.MuiFormLabel-filled': { color: '#2e7d32' },
                            }}
                        >
                            Brand
                        </InputLabel>
                        <Select
                            name="brand"
                            value={formData.brand}
                            onChange={handleChange}
                            required
                            label="Brand"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                color: '#2e7d32',
                            }}
                        >
                            <MenuItem value="" disabled>
                                Select a brand
                            </MenuItem>
                            {brands.map((brand) => (
                                <MenuItem key={brand._id} value={brand.brand_name}>
                                    {brand.brand_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formData.brand && (
                            <Box sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {formData.brand}
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel
                            sx={{
                                color: '#2e7d32',
                                '&.Mui-focused': { color: '#2e7d32' },
                                '&.MuiFormLabel-filled': { color: '#2e7d32' },
                            }}
                        >
                            Category
                        </InputLabel>
                        <Select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                            label="Category"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                color: '#2e7d32',
                            }}
                        >
                            <MenuItem value="" disabled>
                                Select a category
                            </MenuItem>
                            {Array.isArray(categories) && categories.length > 0 ? (
                                categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.name}>
                                        {cat.name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>
                                    No categories available
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formData.category && (
                            <Box sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {formData.category}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel
                            sx={{
                                color: '#2e7d32',
                                '&.Mui-focused': { color: '#2e7d32' },
                                '&.MuiFormLabel-filled': { color: '#2e7d32' },
                            }}
                        >
                            Interests
                        </InputLabel>
                        <Select
                            name="interests"
                            value={formData.interests}
                            onChange={handleChange}
                            required
                            label="Interests"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                color: '#2e7d32',
                            }}
                        >
                            <MenuItem value="" disabled>
                                Select an interest
                            </MenuItem>
                            {interests.map((interest) => (
                                <MenuItem key={interest._id} value={interest.name}>
                                    {interest.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formData.interests.split(',').map((item) => item.trim()).filter(Boolean).map((interest, idx) => (
                            <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {interest}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel
                            sx={{
                                color: '#2e7d32',
                                '&.Mui-focused': { color: '#2e7d32' },
                                '&.MuiFormLabel-filled': { color: '#2e7d32' },
                            }}
                        >
                            Status
                        </InputLabel>
                        <Select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                            label="Status"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32' },
                                color: '#2e7d32',
                            }}
                        >
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="trending">Trending</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            {loading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
            {error && <Alert severity="error">{error || 'Failed to create product'}</Alert>}
            {successMessage && <Alert severity="success">{successMessage}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderColor: '#2e7d32',
                        color: '#2e7d32',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{
                        backgroundColor: '#2e7d32',
                        '&:hover': { backgroundColor: '#45a049' },
                    }}
                    disabled={loading}
                >
                    Add Product
                </Button>
            </Box>
        </Box>
    );
};

export default ProductCreate;